/* eslint-disable import/order */
import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
import CustomAmount from 'components/UI/Modal/customAmount/customAmount'
import { useModalContext } from 'context/modalContext'
import { useHeaderNav } from 'context/headerNavContext'
import { usePageTitle } from 'context/pageTitleContext'
import { useUserContext } from 'context/userContext'
import { getLastPeriodFn } from 'features/AnalyticsPeriodDropdown/AnalyticsPeriodDropdown'
import BarChart from 'features/BarChart/BarChart'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getCurrentBalance, getEarningsGraphData, requestPayout } from 'services/endpoints/earning'
import { useMediaQuery } from 'helpers/hooks/useMediaQuery/useMediaQuery'

import './_earnings.scss'

import { useUserProfile } from 'store'

import { Payouts } from './view/Payouts'
import { ReferralsTab } from './view/referrals'
import { Sales } from './view/Sales'

const chartOptionToTranslateKey = {
  last7Days: 'last7Days',
  last30Days: 'last30Days',
  last12Months: 'last12Months',
}

type BalanceData = {
  message: string
  balance: number
  pending_balance: number
  next_payout_date: string | null
  min_payout_amount: number
}

function formatBalance(val: number | string | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return ''
  const n = Number(val)
  return n % 1 === 0 ? n.toString() : n.toFixed(2)
}

function formatServerDate(
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions
): string {
  if (!iso) return ''
  // parse as local midnight to avoid timezone shifting
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', opts)
}


type ChartOption = keyof typeof chartOptionToTranslateKey

type ChartPeriod = {
  start: string
  end: string
}

function getDatePartsInTimezone(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const getPart = (type: Intl.DateTimeFormatPartTypes): number => {
    return Number(parts.find((part) => part.type === type)?.value)
  }

  return {
    year: getPart('year'),
    month: getPart('month'),
    day: getPart('day'),
  }
}

function formatUtcCalendarDate(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function getChartPeriodInTimezone(
  option: ChartOption,
  timeZone: string
): ChartPeriod {
  /*
   * Get "today" as a calendar date in the user's stored timezone.
   * We then use UTC purely as a safe calendar-date container so DST
   * cannot shift the calculated start or end date.
   */
  const todayParts = getDatePartsInTimezone(new Date(), timeZone)

  const end = new Date(
    Date.UTC(
      todayParts.year,
      todayParts.month - 1,
      todayParts.day
    )
  )

  const start = new Date(end)

  switch (option) {
    case 'last7Days':
      start.setUTCDate(start.getUTCDate() - 6)
      break

    case 'last30Days':
      start.setUTCDate(start.getUTCDate() - 29)
      break

    case 'last12Months':
      start.setUTCMonth(start.getUTCMonth() - 11)
      start.setUTCDate(1)
      break
  }

  return {
    start: formatUtcCalendarDate(start),
    end: formatUtcCalendarDate(end),
  }
}





const SalesChart = () => {
  const { t } = useTranslation()
  const [chartOpen, setChartOpen] = useState<boolean>(false)
  const [chartOptionsOpen, setChartOptionsOpen] = useState<boolean>(false)
  const [selectedChartOption, setSelectedChartOption] =
    useState<keyof typeof chartOptionToTranslateKey>('last7Days')
  const [totalSum, setTotalSum] = useState<number>(0)

  const [isRequestingPayout, setIsRequestingPayout] = useState(false)

  const modalData = useModalContext()
  const userData = useUserProfile()!
  const reportingTimezone = userData?.time_zone ?? 'UTC'
  useUserContext() // kept because you had it; remove if truly unused
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const payoutMethod = userData?.payout_method
  const payoutFrequency = userData?.payout_frequency
  const { id } = useParams<{ id: string }>()
  const isMobile = useMediaQuery('(max-width: 400px)')


  if (id !== 'payouts' && id !== 'sales' && id !== 'referrals') {
    return <Navigate to={'/404'} />
  }

    const { data: balanceData, isLoading: isBalanceLoading } = useQuery<BalanceData>(
    'getCurrentBalance',
    getCurrentBalance
    )

const minAmount = balanceData?.min_payout_amount ?? 0
const chartDatasetOptions = useMemo(() => {
  switch (selectedChartOption) {
    case 'last30Days':
      return {
        barThickness: 5,
        maxBarThickness: 5,
        categoryPercentage: 0.7,
        barPercentage: 0.7,
      }

    case 'last12Months':
      return {
        barThickness: 14,
        maxBarThickness: 14,
      }

    case 'last7Days':
    default:
      return {
        barThickness: 9,
        maxBarThickness: 9,
      }
  }
}, [selectedChartOption])


  const payoutDateFormatOpts: Intl.DateTimeFormatOptions = useMemo(() => {
    const now = new Date()
    const currentYear = now.getFullYear()

    // next_payout_date is `YYYY-MM-DD` from server
    const nextIso = balanceData?.next_payout_date
    const nextYear = nextIso ? new Date(`${nextIso}T00:00:00`).getFullYear() : null

    const showYear = nextYear !== null && nextYear !== currentYear

    if (isMobile) {
      return {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...(showYear ? { year: 'numeric' } : {}),
      }
    }

    return {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      ...(showYear ? { year: 'numeric' } : {}),
    }
  }, [isMobile, balanceData?.next_payout_date])

  const balance = balanceData?.balance ?? 0
  const pendingBalance = balanceData?.pending_balance ?? 0

  const nextPayoutDateLabel = useMemo(() => {
    return formatServerDate(balanceData?.next_payout_date, payoutDateFormatOpts)
  }, [balanceData?.next_payout_date, payoutDateFormatOpts])


const { data: graphData } = useQuery(
  [
    'salesAnalyticsGraph',
    selectedChartOption,
    reportingTimezone,
  ],
  async () => {
    const selectedPeriod = getChartPeriodInTimezone(
      selectedChartOption,
      reportingTimezone
    )

    const resp = await getEarningsGraphData({
      dateFrom: selectedPeriod.start,
      dateTo: selectedPeriod.end,
    })

    const fmt = (iso: string) => {
      /*
       * Backend returns YYYY-MM-DD labels already representing dates
       * in the user's reporting timezone. Parse as local-style calendar
       * dates only for display; do not convert these as timestamps.
       */
      const date = new Date(`${iso}T00:00:00`)

      if (selectedChartOption === 'last7Days') {
        return date.toLocaleDateString('en-US', {
          weekday: 'short',
        })
      }

      if (selectedChartOption === 'last30Days') {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
      })
    }

    const tooltipDate = (iso: string) =>
      new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })

    const rows = resp?.message?.chart?.chart ?? []

    const points = rows.map((row: any) => ({
      x: fmt(row.day),
      y: Number(row.sum) || 0,
      date: tooltipDate(row.day),
    }))

    const total = points.reduce(
      (sum: number, point: any) => sum + point.y,
      0
    )

    setTotalSum(Number(total.toFixed(2)))

    return points
  },
  {
    enabled: !!chartOpen && !!reportingTimezone,
    keepPreviousData: true,
    refetchOnMount: false,
  }
)
  const changeSelectedChartOption = (chartOpt: keyof typeof chartOptionToTranslateKey) => {
    setSelectedChartOption(chartOpt)
    setChartOptionsOpen(false)
  }

const handleRequestPayout = async (funds: number) => {
  if (isRequestingPayout) return null

  setIsRequestingPayout(true)

  try {
    const result = await requestPayout(funds)

    const backendError = result?.data?.message

    if (backendError?.status === 'error') {
      return result
    }

    modalData.clearModal()

    queryClient.invalidateQueries('user-profile')
    queryClient.invalidateQueries('getCurrentBalance')

    return result
  } catch (error: any) {
    console.error('Request payout threw an error:', error)

    return error?.response?.data || {
      data: {
        message: {
          status: 'error',
          message: 'An unexpected error occurred',
          code: 'unexpected_error',
        },
      },
    }
  } finally {
    setIsRequestingPayout(false)
  }
}

  const toggleAmountModal = () => {
    modalData.addModal(
      t('Custom Amount'),
<CustomAmount
  defaultAmount={balance}
  minAmount={minAmount}
  maxAmount={balance}
  onSubmit={handleRequestPayout}
  onClose={() => modalData.clearModal()}
  onPayoutSettings={() => {
    modalData.clearModal()
    navigate('/settings/general/payout-settings/payment-method')
  }}
/>
    )
  }

  const renderButton = () => {
    if (!userData?.payout_method) {
      return (
        <Button
          text={t('addPayoutMethod')}
          color='blue'
          width='fit'
          height='5'
          padding='2'
          font='mont-14-semi-bold'
          clickFn={() => navigate('/settings/general/payout-settings/payment-method')}
        />
      )
    }

    if (userData?.payout_frequency === 'manual') {
      return (
        <Button
          text={t('requestPayout')}
          color='blue'
          width='fit'
          height='5'
          padding='2'
          font='mont-14-semi-bold'
          clickFn={() => {
            // If you still need "manual payout restricted" logic,
            // return a flag in the BALANCE endpoint and check it here.
            if (balance < 20) {
              addToast('error', t('Your current balance is less than 20$'))
              return
            }
            toggleAmountModal()
          }}
        />
      )
    }

    return (
      <div className='balance-payment'>
        <div className='balance-payment-subtext'>{t('upcomingPayment')}</div>
        <div className='balance-payment-text'>{nextPayoutDateLabel}</div>
      </div>
    )
  }

  return (
    <div className='balance-container'>
      {!chartOpen ? (
        <>
          <div className='balance-text'>{t('currentBalance')}</div>

          <div className='balance-number'>
            <spriteIcons.IconDollar width='15' />
            <div className='balance-number-value' style={{ minHeight: '50px' }}>
              {isBalanceLoading ? <>&nbsp;</> : formatBalance(balance)}
            </div>
            <span className='balance-number-currency-text'>(USD)</span>
          </div>

          <div className='balance-pending'>
            Pending{' '}
            <span>
              {isBalanceLoading ? (
                <span style={{ minHeight: '20px', display: 'inline-block' }}>&nbsp;</span>
              ) : (
                `$${Number(pendingBalance).toFixed(2)}`
              )}
            </span>
          </div>

          <div className='balance-options'>
            <Link to='/settings/general/payout-settings' className='balance-settings'>
              <spriteIcons.IconCogWheel />
            </Link>

            <div className='balance-button'>
              {renderButton()}
            </div>

            <IconButton
              icon={<spriteIcons.IconGraph />}
              size='big'
              type='black'
              clickFn={() => setChartOpen(true)}
            />
          </div>
        </>
      ) : (
        <div className='balance-chart'>
          <div className='balance-chart-options'>
            <div className='balance-chart-currency'>
              <div className='balance-number'>
                <spriteIcons.IconDollar width='30' height='30' />
                <div className='balance-amount'>
                  <div className='balance-number-graph-open'>
                    {Number(totalSum).toFixed(2)}
                  </div>
                  <span className='balance-usd'>(USD)</span>
                </div>
              </div>
              <div className='balance-overall'>{t('overallRevenue')}</div>
            </div>

            <div
              className='balance-chart-select'
              onClick={() => setChartOptionsOpen(!chartOptionsOpen)}
            >
              <div className='balance-chart-selected'>
                {t('by')}{' '}
                <span>{t(chartOptionToTranslateKey[selectedChartOption])}</span>
              </div>

              <div
                className={`balance-chart-select-list ${
                  chartOptionsOpen && 'balance-chart-select-list-open'
                }`}
              >
                <div
                  className='balance-chart-select-option'
                  onClick={() => changeSelectedChartOption('last7Days')}
                >
                  {t('by')} <span>{t('last7Days')}</span>
                </div>
                <div
                  className='balance-chart-select-option'
                  onClick={() => changeSelectedChartOption('last30Days')}
                >
                  {t('by')} <span>{t('last30Days')}</span>
                </div>
                <div
                  className='balance-chart-select-option'
                  onClick={() => changeSelectedChartOption('last12Months')}
                >
                  {t('by')} <span>{t('last12Months')}</span>
                </div>
              </div>
            </div>

            <div className='balance-chart-close' onClick={() => setChartOpen(false)}>
              <spriteIcons.IconClose />
            </div>
          </div>

<BarChart
  chartData={graphData}
  datasetOptions={chartDatasetOptions}
/>
        </div>
      )}
    </div>
  )
}


export const Earnings = () => {
    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const { setHeaderNav } = useHeaderNav()
    const { setTitle } = usePageTitle()

    if (id !== 'payouts' && id !== 'sales' && id !== 'referrals') {
        return <Navigate to={'/404'} />
    }
    const earningsNavUrls = [
        '/earnings/sales',
        '/earnings/payouts',
        '/earnings/referrals'
    ]

    useEffect(() => {
        setTitle(t('earnings'))
        setHeaderNav(earningsNavUrls)

        return () => {
        setHeaderNav([]) // Clear nav on unmount
        }
    }, [setTitle, setHeaderNav, t])

    
    const renderContent = () => {
        if (id === 'payouts') {
            return <Payouts />
        } else if (id === 'sales') {
            return (
                <>
                    <SalesChart />
                    <WithHeaderSection
                        customClass='sales-whs'
                        headerSection={
                            <h1 style={{ marginBottom: 0 }}>
                                {t('transactions')}
                            </h1>
                        }
                    >
                        <Sales />
                    </WithHeaderSection>
                </>
            )
        } else if (id === 'referrals') {
            return <ReferralsTab />
        }
    }

    return (
        <>
            <div className={`earnings ${id === 'referrals' ? 'earnings--referrals' : ''}`}>
              {renderContent()}
            </div>
            <DesktopAdditionalContent />
        </>
    )
}
